import React, { useState, useEffect } from 'react';

function DimensionsManager() {
  const [dimensions, setDimensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  const emptyDimension = {
    name: '',
    description: '',
    visualization_type: 'spectrum',
    categories: [
      { value: '', label: '', description: '' }
    ]
  };

  const [formData, setFormData] = useState(emptyDimension);

  // Helper to ensure categories is an array
  const parseCategories = (categories) => {
    if (typeof categories === 'string') {
      return JSON.parse(categories);
    }
    return categories || [];
  };

  useEffect(() => {
    fetchDimensions();
  }, []);

  const fetchDimensions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/law-dimensions`);
      const data = await response.json();

      if (data.success) {
        setDimensions(data.dimensions);
      } else {
        setError(data.error || 'Failed to load dimensions');
      }
    } catch (err) {
      setError('Failed to load dimensions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editing
        ? `${API_BASE}/api/law-dimensions/${editing.id}`
        : `${API_BASE}/api/law-dimensions`;

      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        fetchDimensions();
        setShowForm(false);
        setEditing(null);
        setFormData(emptyDimension);
      } else {
        alert('Error: ' + (data.error || 'Failed to save dimension'));
      }
    } catch (err) {
      alert('Error saving dimension: ' + err.message);
    }
  };

  const handleEdit = (dimension) => {
    setEditing(dimension);
    setFormData({
      name: dimension.name,
      description: dimension.description,
      visualization_type: dimension.visualization_type,
      categories: parseCategories(dimension.categories)
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setFormData(emptyDimension);
  };

  const addCategory = () => {
    setFormData({
      ...formData,
      categories: [...formData.categories, { value: '', label: '', description: '' }]
    });
  };

  const updateCategory = (index, field, value) => {
    const newCategories = [...formData.categories];
    newCategories[index][field] = value;
    setFormData({ ...formData, categories: newCategories });
  };

  const removeCategory = (index) => {
    if (formData.categories.length <= 1) {
      alert('Dimension must have at least one category');
      return;
    }
    const newCategories = formData.categories.filter((_, i) => i !== index);
    setFormData({ ...formData, categories: newCategories });
  };

  const toggleActive = async (dimension) => {
    try {
      const response = await fetch(`${API_BASE}/api/law-dimensions/${dimension.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...dimension,
          categories: parseCategories(dimension.categories),
          is_active: !dimension.is_active
        })
      });

      const data = await response.json();

      if (data.success) {
        fetchDimensions();
      } else {
        alert('Error: ' + (data.error || 'Failed to toggle dimension'));
      }
    } catch (err) {
      alert('Error toggling dimension: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading dimensions...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Law Analysis Dimensions</h2>
          <p className="text-gray-600 mt-1">
            Manage the analysis dimensions used for law analysis
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Add Dimension
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Dimension Form */}
      {showForm && (
        <div className="mb-6 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {editing ? 'Edit Dimension' : 'New Dimension'}
          </h3>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimension Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visualization Type
                </label>
                <select
                  value={formData.visualization_type}
                  onChange={(e) => setFormData({ ...formData, visualization_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="spectrum">Spectrum</option>
                  <option value="impact-chart">Impact Chart</option>
                  <option value="funding-breakdown">Funding Breakdown</option>
                  <option value="risk-matrix">Risk Matrix</option>
                </select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Categories
                  </label>
                  <button
                    type="button"
                    onClick={addCategory}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    + Add Category
                  </button>
                </div>

                <div className="space-y-3">
                  {formData.categories.map((category, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Value</label>
                          <input
                            type="text"
                            value={category.value}
                            onChange={(e) => updateCategory(index, 'value', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="e.g., high-risk"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Label</label>
                          <input
                            type="text"
                            value={category.label}
                            onChange={(e) => updateCategory(index, 'label', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="e.g., High Risk"
                            required
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeCategory(index)}
                            className="px-3 py-1 text-sm text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="block text-xs text-gray-600 mb-1">Description</label>
                        <textarea
                          value={category.description}
                          onChange={(e) => updateCategory(index, 'description', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          rows={2}
                          placeholder="Explain what this category means..."
                          required
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editing ? 'Update Dimension' : 'Create Dimension'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Dimensions List */}
      <div className="space-y-4">
        {dimensions.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No dimensions found. Create one to get started.</p>
          </div>
        ) : (
          dimensions.map((dimension) => (
            <div key={dimension.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">{dimension.name}</h3>
                  <p className="text-gray-600 mt-1">{dimension.description}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Visualization: <span className="font-medium">{dimension.visualization_type}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    dimension.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {dimension.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => toggleActive(dimension)}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    {dimension.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleEdit(dimension)}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Edit
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Categories ({parseCategories(dimension.categories).length})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {parseCategories(dimension.categories).map((cat, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded border border-gray-200">
                      <p className="font-medium text-sm text-gray-900">{cat.label}</p>
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-mono">{cat.value}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{cat.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default DimensionsManager;
